import {
  parseLoginResponse,
  parseWaitlistActionResponse,
  parseWaitlistEntryDetailResponse,
  parseWaitlistEntryListResponse,
  parseWaitlistInviteValidateResponse,
  parseWaitlistJoinResponse,
  parseWaitlistSendRegisterCodeResponse,
} from '@drama/contracts'

export const WAITLIST_ROUTE_IDS = Object.freeze({
  publicJoin: 'waitlist.public.join',
  publicInviteValidate: 'waitlist.public.validateInvite',
  publicSendRegisterCode: 'waitlist.public.sendRegisterCode',
  registerWithInvite: 'waitlist.public.registerWithInvite',
  adminList: 'waitlist.admin.list',
  adminDetail: 'waitlist.admin.detail',
  adminCreate: 'waitlist.admin.create',
  adminNote: 'waitlist.admin.note',
  adminInvite: 'waitlist.admin.invite',
  adminResend: 'waitlist.admin.resend',
  adminReject: 'waitlist.admin.reject',
  adminExport: 'waitlist.admin.export',
})

/**
 * @param {Record<string, any>} query
 */
function buildWaitlistQuery(query) {
  const params = {}
  if (query.status !== undefined) params.status = query.status
  if (query.source !== undefined) params.source = query.source
  if (query.use_case !== undefined) params.use_case = query.use_case
  if (query.q !== undefined) params.q = query.q
  if (query.cursor !== undefined) params.cursor = query.cursor
  if (query.limit !== undefined) params.limit = query.limit
  return params
}

/**
 * @callchain.routeId waitlist.public.join
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {Record<string, any>} input
 */
export function joinWaitlist(transport, input) {
  return transport.request('POST', '/api/visual-game/waitlist', {
    body: input,
    public: true,
    parse: parseWaitlistJoinResponse,
    routeId: WAITLIST_ROUTE_IDS.publicJoin,
  })
}

/**
 * @callchain.routeId waitlist.public.validateInvite
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {string} token
 */
export function validateWaitlistInvite(transport, token) {
  return transport.request('GET', `/api/visual-game/waitlist/invites/${encodeURIComponent(token)}`, {
    public: true,
    parse: parseWaitlistInviteValidateResponse,
    routeId: WAITLIST_ROUTE_IDS.publicInviteValidate,
  })
}

/**
 * @callchain.routeId waitlist.public.sendRegisterCode
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {{ email?: string, invite_token: string, locale?: string }} input
 */
export function sendWaitlistRegisterCode(transport, input) {
  return transport.request('POST', '/api/sk/auth/send-invite-code', {
    body: input,
    public: true,
    parse: parseWaitlistSendRegisterCodeResponse,
    routeId: WAITLIST_ROUTE_IDS.publicSendRegisterCode,
  })
}

/**
 * @callchain.routeId waitlist.public.registerWithInvite
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {Record<string, any>} input
 */
export function registerWithInvite(transport, input) {
  return transport.request('POST', '/api/sk/auth/register', {
    body: input,
    public: true,
    parse: parseLoginResponse,
    routeId: WAITLIST_ROUTE_IDS.registerWithInvite,
  })
}

/**
 * @callchain.routeId waitlist.admin.list
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {Record<string, any>} [query]
 */
export function listWaitlistEntries(transport, query = {}) {
  return transport.request('GET', '/api/admin/visual-game/waitlist', {
    query: buildWaitlistQuery(query),
    parse: parseWaitlistEntryListResponse,
    routeId: WAITLIST_ROUTE_IDS.adminList,
  })
}

/**
 * @callchain.routeId waitlist.admin.detail
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {number} id
 */
export function getWaitlistEntry(transport, id) {
  return transport.request('GET', `/api/admin/visual-game/waitlist/${encodeURIComponent(id)}`, {
    parse: parseWaitlistEntryDetailResponse,
    routeId: WAITLIST_ROUTE_IDS.adminDetail,
  })
}

/**
 * @callchain.routeId waitlist.admin.create
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {Record<string, any>} input
 */
export function createManualWaitlistEntry(transport, input) {
  return transport.request('POST', '/api/admin/visual-game/waitlist', {
    body: input,
    parse: parseWaitlistActionResponse,
    routeId: WAITLIST_ROUTE_IDS.adminCreate,
  })
}

/**
 * @callchain.routeId waitlist.admin.note
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {number} id
 * @param {Record<string, any>} input
 */
export function addWaitlistNote(transport, id, input) {
  return transport.request('POST', `/api/admin/visual-game/waitlist/${encodeURIComponent(id)}/note`, {
    body: input,
    parse: parseWaitlistActionResponse,
    routeId: WAITLIST_ROUTE_IDS.adminNote,
  })
}

/**
 * @callchain.routeId waitlist.admin.invite
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {number} id
 * @param {Record<string, any>} input
 */
export function inviteWaitlistEntry(transport, id, input = {}) {
  return transport.request('POST', `/api/admin/visual-game/waitlist/${encodeURIComponent(id)}/invite`, {
    body: input,
    parse: parseWaitlistActionResponse,
    routeId: WAITLIST_ROUTE_IDS.adminInvite,
  })
}

/**
 * @callchain.routeId waitlist.admin.resend
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {number} id
 * @param {Record<string, any>} input
 */
export function resendWaitlistInvite(transport, id, input = {}) {
  return transport.request('POST', `/api/admin/visual-game/waitlist/${encodeURIComponent(id)}/resend`, {
    body: input,
    parse: parseWaitlistActionResponse,
    routeId: WAITLIST_ROUTE_IDS.adminResend,
  })
}

/**
 * @callchain.routeId waitlist.admin.reject
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {number} id
 * @param {Record<string, any>} input
 */
export function rejectWaitlistEntry(transport, id, input) {
  return transport.request('POST', `/api/admin/visual-game/waitlist/${encodeURIComponent(id)}/reject`, {
    body: input,
    parse: parseWaitlistActionResponse,
    routeId: WAITLIST_ROUTE_IDS.adminReject,
  })
}

/**
 * @callchain.routeId waitlist.admin.export
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 * @param {Record<string, any>} [query]
 */
export function exportWaitlistEntries(transport, query = {}) {
  return transport.text('GET', '/api/admin/visual-game/waitlist/export', {
    query: buildWaitlistQuery(query),
    routeId: WAITLIST_ROUTE_IDS.adminExport,
  })
}

/**
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 */
export function createWaitlistApi(transport) {
  return {
    public: {
      join(input) {
        return joinWaitlist(transport, input)
      },
      validateInvite(token) {
        return validateWaitlistInvite(transport, token)
      },
      sendRegisterCode(input) {
        return sendWaitlistRegisterCode(transport, input)
      },
      registerWithInvite(input) {
        return registerWithInvite(transport, input)
      },
    },
    admin: {
      listEntries(query) {
        return listWaitlistEntries(transport, query)
      },
      getEntry(id) {
        return getWaitlistEntry(transport, id)
      },
      createManualEntry(input) {
        return createManualWaitlistEntry(transport, input)
      },
      addNote(id, input) {
        return addWaitlistNote(transport, id, input)
      },
      invite(id, input) {
        return inviteWaitlistEntry(transport, id, input)
      },
      resendInvite(id, input) {
        return resendWaitlistInvite(transport, id, input)
      },
      reject(id, input) {
        return rejectWaitlistEntry(transport, id, input)
      },
      exportEntries(query) {
        return exportWaitlistEntries(transport, query)
      },
    },
  }
}
